// AppSync GraphQL client. Axios-based to match JstVerify's pattern.
// One `gql<T>(query, variables)` helper for both queries and mutations
// — AppSync HTTP transport doesn't distinguish them at the wire level.
//
// Token plumbing:
//   - `setAuthToken(token | null)` updates the Authorization header
//     on the shared axios instance. Called on signin success and on
//     signout. The AuthContext is the single caller.
//   - We do NOT auto-load the token from localStorage here — that's
//     also the AuthContext's job. Keeping it out of this module makes
//     `api.ts` purely transport.

import axios, { type AxiosInstance } from "axios";

const ENDPOINT = import.meta.env.VITE_APPSYNC_URL;
if (!ENDPOINT) {
  // Surfaced loudly during dev; build won't fail (build-time env replace
  // happens at build, not at module-load), but localhost dev without a
  // .env will see this warning in the console.
  // eslint-disable-next-line no-console
  console.warn("VITE_APPSYNC_URL is not set; see .env.example");
}

// Sentinel value used in the Authorization header for unauthenticated
// requests (e.g., calling `signin` before we have a JWT). AppSync's
// AWS_LAMBDA auth REJECTS the request immediately (before invoking the
// authorizer Lambda) if there's no Authorization header at all, so we
// always send one — the team JWT authorizer recognizes this sentinel
// (or any non-Bearer value on the `signin` mutation) and allows it.
const UNAUTHENTICATED_TOKEN = "anonymous";

const client: AxiosInstance = axios.create({
  baseURL: ENDPOINT,
  headers: {
    "Content-Type": "application/json",
    // NOTE: do NOT set Authorization here. In axios 1.x, headers passed
    // to `create()` land at `defaults.headers.Authorization` (root level)
    // and silently override any value at `defaults.headers.common
    // .Authorization` for outgoing requests. setAuthToken() writes to
    // `.common`, so co-locating both at create-time would mean later
    // .common writes are ignored — every authenticated request would
    // ship the create-time value forever. Initialize via setAuthToken(null)
    // below so there is exactly one source of truth.
  },
  // AppSync responds with errors as 200 + body.errors, so we never
  // expect non-2xx; let axios throw on >=400 only for transport issues.
  validateStatus: (s) => s >= 200 && s < 300,
});

export function setAuthToken(token: string | null): void {
  if (token) {
    client.defaults.headers.common["Authorization"] = token.startsWith(
      "Bearer ",
    )
      ? token
      : `Bearer ${token}`;
  } else {
    // Sentinel rather than deletion — see the comment on UNAUTHENTICATED_TOKEN.
    client.defaults.headers.common["Authorization"] = UNAUTHENTICATED_TOKEN;
  }
}

// Seed the initial Authorization header through the same code path the
// AuthContext uses for updates. Guarantees `defaults.headers.common
// .Authorization` is always populated, and ensures subsequent updates
// take effect (no shadowing by a root-level header).
setAuthToken(null);

export class GraphQLError extends Error {
  errors: { message: string; errorType?: string; path?: unknown }[];
  constructor(
    errors: { message: string; errorType?: string; path?: unknown }[],
  ) {
    super(errors.map((e) => e.message).join("; "));
    this.name = "GraphQLError";
    this.errors = errors;
  }
}

/** Send a GraphQL query/mutation. Throws on network error or GraphQL error. */
export async function gql<TData>(
  query: string,
  variables: Record<string, unknown> = {},
): Promise<TData> {
  const res = await client.post("", { query, variables });
  const body = res.data as {
    data?: TData;
    errors?: { message: string; errorType?: string; path?: unknown }[];
  };
  if (body.errors && body.errors.length > 0) {
    throw new GraphQLError(body.errors);
  }
  if (!body.data) {
    throw new Error("GraphQL response has no data and no errors.");
  }
  return body.data;
}
